if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.onSubmitHandler = this.onSubmitHandler.bind(this);
        this.form.addEventListener('submit', this.onSubmitHandler);
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');
        this.hideErrors = this.dataset.hideErrors === 'true';
        this._activeCrossellVariantId = null;
        this.bindQuantityListeners();
      }

      connectedCallback() {
        this.bindCrossellCheckbox();
      }

      getCrossellScope() {
        const modal = this.closest('quick-add-modal');

        if (modal) {
          return modal.querySelector('[id^="QuickAddInfo-"]') || modal;
        }

        return (
          this.closest('product-info') ||
          this.closest('.main-product') ||
          this.closest('.product') ||
          this.closest('.featured-product') ||
          this.closest('.shopify-section') ||
          this.closest('[id^="shopify-section-"]')
        );
      }

      bindCrossellCheckbox() {
        const scope = this.getCrossellScope();
        if (!scope) return;

        scope.querySelectorAll('input[data-crossell-variant-id]').forEach((input) => {
          if (input.dataset.crossellBound === 'true') return;
          input.dataset.crossellBound = 'true';

          const syncCrossell = () => {
            const variantId =
              input.getAttribute('data-crossell-variant-id') || input.dataset.crossellVariantId;

            this._activeCrossellVariantId = input.checked && variantId ? variantId : null;
          };

          input.addEventListener('change', syncCrossell);
          syncCrossell();
        });
      }

      bindQuantityListeners() {
        const formId = this.form.getAttribute('id');
        const quantityInputs = new Set([
          ...this.form.querySelectorAll('input[name="quantity"]'),
          ...(formId
            ? document.querySelectorAll(`input[name="quantity"][form="${formId}"]`)
            : []),
        ]);

        quantityInputs.forEach((input) => {
          input.addEventListener('change', this.onFormStateReset.bind(this));
          input.addEventListener('input', this.onFormStateReset.bind(this));
        });
      }

      onFormStateReset() {
        this.error = false;
        this.handleErrorMessage();
        this.enableSubmitButton();
      }

      enableSubmitButton() {
        if (!this.submitButton || this.submitButton.hasAttribute('disabled')) return;

        const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
        if (soldOutMessage && !soldOutMessage.classList.contains('hidden')) return;

        this.submitButton.removeAttribute('aria-disabled');

        const labelSpan = this.submitButton.querySelector(
          'span:not(.sold-out-message):not(.loading-overlay__spinner)'
        );

        if (soldOutMessage && labelSpan) {
          soldOutMessage.classList.add('hidden');
          labelSpan.classList.remove('hidden');
        }
      }

      getCartResponseMessage(response) {
        const errors = response?.errors;
        const errorsText =
          typeof errors === 'string'
            ? errors
            : errors && typeof errors === 'object'
              ? Object.values(errors).flat().join(' ')
              : '';

        return `${response?.description || ''} ${response?.message || ''} ${errorsText}`.toLowerCase();
      }

      isPartialCartAdd(response) {
        if (!response || typeof response !== 'object') return false;

        const message = this.getCartResponseMessage(response);
        const hasPartialMessage =
          (message.includes('only') && message.includes('added')) ||
          message.includes('due to availability');

        if (hasPartialMessage) return true;

        const hasItems =
          Number(response.item_count) > 0 ||
          (Array.isArray(response.items) && response.items.length > 0);

        return (
          hasItems &&
          typeof response.status === 'number' &&
          response.status >= 400
        );
      }

      parseCartResponse(response) {
        if (!response || typeof response.text !== 'function') {
          return Promise.resolve(response);
        }

        return response.text().then((text) => {
          if (!text) return { status: 'error', description: 'Empty cart response' };
          try {
            return JSON.parse(text);
          } catch (error) {
            console.error('Cart response parse error:', error);
            return { status: 'error', description: 'Invalid cart response' };
          }
        });
      }

      isCartError(response) {
        if (this.isPartialCartAdd(response)) return false;
        if (!response || typeof response !== 'object') return true;
        if (response.status === 'error') return true;
        if (typeof response.status === 'number' && response.status >= 400) return true;
        return false;
      }

      getAddedLineQuantity(response, variantId, fallbackQuantity) {
        if (!response?.items?.length || !variantId) return fallbackQuantity;

        const lineItem = response.items.find(
          (item) => String(item.variant_id) === String(variantId)
        );

        return lineItem?.quantity ?? fallbackQuantity;
      }

      isCartPage() {
        const cartPath = window.routes?.cart_url || '/cart';
        return (
          window.location.pathname === cartPath ||
          !!document.querySelector('cart-items.main-cart-page')
        );
      }

      getDrawerSectionIds() {
        if (!this.cart?.getSectionsToRender) return [];
        return this.cart.getSectionsToRender().map((section) => section.id);
      }

      getAllCartSectionIds() {
        const ids = new Set(this.getDrawerSectionIds());

        if (this.isCartPage()) {
          ids.add('main-cart-items');
        }

        return [...ids];
      }

      hasValidDrawerSections(response) {
        if (!response?.sections || !this.cart) return false;

        return this.getDrawerSectionIds().every((sectionId) => response.sections[sectionId]);
      }

      renderCartSections(response) {
        if (!this.cart?.renderContents) return Promise.resolve(response);

        const render = (cartState) => {
          if (!this.hasValidDrawerSections(cartState)) return;
          this.cart.renderContents(cartState);
        };

        if (this.hasValidDrawerSections(response)) {
          render(response);
          return Promise.resolve(response);
        }

        return this.refreshCartSections(response || {}).then((refreshed) => {
          render(refreshed);
          return refreshed;
        });
      }

      refreshCartPage() {
        const cartItems = document.querySelector('cart-items.main-cart-page');

        if (!cartItems) return Promise.resolve();

        return fetch(`${routes.cart_url}?section_id=main-cart-items`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const source = html.querySelector('cart-items');

            if (source) {
              cartItems.innerHTML = source.innerHTML;
            }

            const footer = document.getElementById('main-cart-footer');
            const sourceFooter = html.getElementById('main-cart-footer');

            if (footer && sourceFooter) {
              footer.innerHTML = sourceFooter.innerHTML;
              footer.classList.toggle('is-empty', sourceFooter.classList.contains('is-empty'));
            }

            cartItems.classList.toggle('is-empty', source?.classList.contains('is-empty'));
          })
          .catch((error) => console.error(error));
      }

      updateAllCartUI(cartState) {
        const tasks = [this.refreshCartPage()];

        if (this.cart?.renderContents && !this.isCartPage()) {
          tasks.push(this.renderCartSections(cartState));
        } else if (this.cart?.renderContents && this.hasValidDrawerSections(cartState)) {
          tasks.push(this.renderCartSections(cartState));
        }

        return Promise.all(tasks).then(() => cartState);
      }

      syncCartAfterAdd(response, variantId, { forceRefresh = false } = {}) {
        const cartState = response || {};

        if (forceRefresh || !this.hasValidDrawerSections(cartState)) {
          return this.refreshCartSections(cartState);
        }

        if (cartState.item_count == null) {
          return fetch(`${routes.cart_url}.js`)
            .then((res) => res.json())
            .then((cart) => ({
              ...cartState,
              item_count: cart.item_count,
              items: cart.items,
            }))
            .catch(() => cartState);
        }

        return Promise.resolve(cartState);
      }

      getFormQuantity(formData) {
        let quantity = parseInt(formData.get('quantity'), 10);
        if (Number.isFinite(quantity) && quantity > 0) return quantity;

        const formId = this.form.getAttribute('id');
        const sectionRoot =
          this.closest('.shopify-section') || this.closest('[id^="shopify-section-"]');
        const quantityInput =
          this.form.querySelector('[name="quantity"]') ||
          (formId && document.querySelector(`input[name="quantity"][form="${formId}"]`)) ||
          sectionRoot?.querySelector('input[name="quantity"]');

        quantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;
        return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
      }

      getCrossellVariantId() {
        if (this._activeCrossellVariantId) return this._activeCrossellVariantId;

        const scope = this.getCrossellScope();
        if (!scope) return null;

        for (const input of scope.querySelectorAll('input[data-crossell-variant-id]')) {
          if (!input.checked) continue;

          const variantId =
            input.getAttribute('data-crossell-variant-id') || input.dataset.crossellVariantId;

          if (variantId) return variantId;
        }

        return null;
      }

      buildCartAddItems(formData, quantity, crossellVariantId) {
        const variantId =
          formData.get('id') ||
          this.form.querySelector('[name="id"]')?.value ||
          this.form.querySelector('.product-variant-id')?.value;

        const items = [{ id: variantId, quantity }];

        if (crossellVariantId) {
          items.push({ id: crossellVariantId, quantity });
        }

        const properties = {};

        for (const [key, value] of formData.entries()) {
          if (!key.startsWith('properties[')) continue;

          const propertyKey = key.slice('properties['.length, -1);
          if (propertyKey) properties[propertyKey] = value;
        }

        if (Object.keys(properties).length) {
          items[0].properties = properties;
        }

        const sellingPlan = formData.get('selling_plan');
        if (sellingPlan) items[0].selling_plan = sellingPlan;

        return items;
      }

      postCartAdd(body) {
        return fetch(`${routes.cart_add_url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        }).then((response) => this.parseCartResponse(response));
      }

      addToCart(formData, quantity, crossellVariantId) {
        const sectionIds = this.getCartSectionIds();

        if (crossellVariantId) {
          const body = {
            items: this.buildCartAddItems(formData, quantity, crossellVariantId),
          };

          if (sectionIds) {
            body.sections = sectionIds.split(',');
            body.sections_url = window.location.pathname;
          }

          return this.postCartAdd(body);
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        if (this.cart) {
          if (sectionIds) {
            formData.append('sections', sectionIds);
          }
          formData.append('sections_url', window.location.pathname);
          const active = document.activeElement;
          let focusReturn = active;
          if (active === this.submitButton) {
            focusReturn =
              document.querySelector('#cart-icon-bubble') ||
              document.querySelector('#cart-icon-bubble-sticky') ||
              active;
          }
          this.cart.setActiveElement(focusReturn);
        }

        config.body = formData;
        return fetch(`${routes.cart_add_url}`, config).then((response) =>
          this.parseCartResponse(response)
        );
      }

      getCartSectionIds() {
        return this.getAllCartSectionIds().join(',');
      }

      refreshCartSections(cartResponse = {}) {
        const sectionIds = this.getCartSectionIds();
        const cartState = { ...cartResponse };

        const sectionsPromise = sectionIds
          ? fetch(`${routes.cart_url}?sections=${sectionIds}`).then((res) => res.json())
          : Promise.resolve({});

        const cartJsonPromise = fetch(`${routes.cart_url}.js`).then((res) => res.json());

        return Promise.all([sectionsPromise, cartJsonPromise])
          .then(([sections, cart]) => {
            cartState.sections = { ...(cartState.sections || {}), ...sections };
            cartState.item_count = cart.item_count;
            cartState.items = cart.items;
            return cartState;
          })
          .catch(() => cartState);
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.error = false;
        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        if (this.querySelector('.loading-overlay__spinner')) {
          this.querySelector('.loading-overlay__spinner').classList.remove('hidden');
        }

        const formData = new FormData(this.form);
        const quantity = this.getFormQuantity(formData);
        const crossellVariantId = this.getCrossellVariantId();

        if (this.cart && crossellVariantId) {
          const active = document.activeElement;
          let focusReturn = active;
          if (active === this.submitButton) {
            focusReturn =
              document.querySelector('#cart-icon-bubble') ||
              document.querySelector('#cart-icon-bubble-sticky') ||
              active;
          }
          this.cart.setActiveElement(focusReturn);
        }

        this.addToCart(formData, quantity, crossellVariantId).then((response) => {
            const isPartial = this.isPartialCartAdd(response);
            const isError = this.isCartError(response);

            if (isError) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description || response.message);
              this.error = true;

              const errorText = this.getCartResponseMessage(response);
              const isSoldOut = /sold out/.test(errorText) && !isPartial;
              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');

              if (soldOutMessage && isSoldOut) {
                this.submitButton.setAttribute('aria-disabled', true);
                const labelSpan = this.submitButton.querySelector(
                  'span:not(.sold-out-message):not(.loading-overlay__spinner)'
                );
                if (labelSpan) labelSpan.classList.add('hidden');
                soldOutMessage.classList.remove('hidden');
              }

              if (!isPartial) return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (isPartial) {
              this.handleErrorMessage(response.description || response.message);
              this.error = false;
            }

            const variantId = formData.get('id');

            const finishAdd = (cartState) => {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: cartState,
              });

              if (this.cart?.classList.contains('is-empty') && Number(cartState?.item_count) > 0) {
                this.cart.classList.remove('is-empty');
              }

              const quickAddModal = this.closest('quick-add-modal');
              const updateCartUI = () => this.updateAllCartUI(cartState);

              return updateCartUI().then(() => {
                if (quickAddModal) {
                  quickAddModal.hide(true);
                }

                document.dispatchEvent(
                  new CustomEvent('ingenio:cart-add-rendered', {
                    detail: {
                      family: this.dataset.crossSellFamily || 'none',
                      productId: this.dataset.crossSellProductId || '',
                      variantId: String(variantId || ''),
                      itemCount: Number(cartState?.item_count || 0),
                    },
                  }),
                );

                return cartState;
              });
            };

            return this.syncCartAfterAdd(response, variantId, { forceRefresh: isPartial }).then(
              (cartState) => finishAdd(cartState)
            );
          })
          .catch((e) => {
            console.error(e);
            this.error = true;
            this.handleErrorMessage();
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (!this.error && this.cart?.classList.contains('is-empty')) {
              this.cart.classList.remove('is-empty');
            }
            this.enableSubmitButton();
            if (this.querySelector('.loading-overlay__spinner') != null) {
              this.querySelector('.loading-overlay__spinner').classList.add('hidden');
            }
            if (this.submitButton && document.activeElement === this.submitButton) {
              this.submitButton.blur();
            }
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}
